import { useEffect, useState } from "react";
import useAvoidFirstMount from "hooks/useMountedState.js";
import Icon from "components/Elements/Icon/Icon";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import useSubscribersOnlyOption from "components/Pages/Home/Helpers/useSubOnlyNotification.tsx";
import { setBodyValue } from "components/Pages/Home/UploadForms/formsFields.js";
import { Toggle } from "components/Elements/Toggle/Toggle.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useCustomController } from "../useCustomController.js";

import "./reusableFields.scss";

interface ISettingsItemProps {
  textKey: string;
  icon: string;
  subcribersOnly: boolean;
  disableOption?: {
    fieldName: string;
    conditionFunc: Function;
    tooltipOnDisable: boolean;
    turnOnDisable: boolean;
  };
  getValues: () => any;
  tooltip?: string;
  clickableTooltip?: boolean;
}

const SettingsItem = ({
  textKey,
  icon,
  subcribersOnly,
  ...props
}: ISettingsItemProps) => {
  const { fieldControls } = useCustomController(props);

  const [toggleStatus, setToggleStatus] = useState(fieldControls.value);
  const { getTextByKey, getHtmlByKey } = useContent();

  useEffect(() => {
    fieldControls.onChange(toggleStatus);
    // setting the value in the body after changing the toggle status
    setBodyValue(props, toggleStatus);
  }, [toggleStatus]);

  // handle disable option in case this field depends on another field value
  const [disable, setDisable] = useState(false);

  const { SubOnlyNotification, handleSetStatus } = useSubscribersOnlyOption({
    callbackFunc: setToggleStatus,
    subcribersOnly,
    disabled: disable,
    titleKey: textKey,
  });

  useEffect(() => {
    if (props.disableOption) {
      const { fieldName, conditionFunc } = props.disableOption;
      const value = props.getValues()[fieldName];
      if (conditionFunc) {
        const conditionToDisable = conditionFunc(value);
        setDisable(conditionToDisable);
      } else {
        const exist = value?.length > 0;
        setDisable(!exist);
      }
    }
  }, [props.getValues()[props.disableOption?.fieldName]]);

  // changing the toggle status in case the disable option chaning
  useAvoidFirstMount({
    callback: () => {
      if (props.disableOption) {
        if (props.disableOption.turnOnDisable) {
          setToggleStatus(disable);
        } else {
          setToggleStatus(!disable);
        }
      }
    },
    dependencies: [disable],
  });

  return (
    <>
      <div className={`security-option${disable ? " disabled-field" : ""}`}>
        {icon ? (
          <Icon name={icon} gray iconClass="security-option-icon" />
        ) : null}
        <div className="security-option-text">{getTextByKey(textKey)}</div>
        <div className="flex-spacer" />
        <SubOnlyNotification />
        <Toggle status={toggleStatus} setStatus={handleSetStatus} />
        {props.tooltip &&
        (!props.disableOption?.tooltipOnDisable || disable) ? (
          // @ts-ignore
          <Tooltip
            tooltipText={getHtmlByKey(props.tooltip)}
            clickable={props.clickableTooltip}
          >
            <Icon name="i" gray />
          </Tooltip>
        ) : null}
      </div>
    </>
  );
};

export default SettingsItem;
